import Link from '@/components/Link';
import gtm from '@/lib/gtm';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize } from '@/styles/typography/utils';
import { useInView } from 'framer-motion';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

export type AccordionItem = {
  id?: string | number;
  title: string;
  content: ReactNode;
  autoAdvanceSeconds?: number;
  cta?: {
    href: string;
    text: string;
    onClick?: () => void;
  };
};

export interface AccordionProps {
  activeIndex?: number;
  autoAdvance?: boolean;
  autoAdvanceSeconds?: number;
  eventCategory?: string;
  items: AccordionItem[];
  onChangeActiveIndex?: (index: number) => void;
}

const Accordion: FC<AccordionProps> = ({ autoAdvance = true, autoAdvanceSeconds = 6, ...props }) => {
  const ref = useRef<HTMLOListElement>(null);
  const isInView = useInView(ref, { once: false, amount: 0.5 });
  const [shouldAutoAdvance, setShouldAutoAdvance] = useState(autoAdvance);
  const [activeIndex, setActiveIndex] = useState<number>(props.activeIndex!);

  useEffect(() => {
    if (props.onChangeActiveIndex) {
      props.onChangeActiveIndex(activeIndex);
    }
  }, [activeIndex]);

  useEffect(() => {
    if (typeof props.activeIndex === 'number') {
      if (props.activeIndex !== activeIndex) {
        setActiveIndex(props.activeIndex);
      }
    }
  }, [props.activeIndex]);

  useEffect(() => {
    if (autoAdvance && !isInView && !shouldAutoAdvance) {
      setShouldAutoAdvance(true);
    }
  }, [isInView, shouldAutoAdvance, autoAdvance]);

  return (
    <List ref={ref}>
      {props.items.map((item, index) => (
        <ListItem key={item.id ?? item.title} data-expanded={activeIndex === index} animated={shouldAutoAdvance}>
          {isInView && shouldAutoAdvance && activeIndex === index ? (
            <ListItemProgress
              css={{ '--auto-advance-seconds': `${item.autoAdvanceSeconds || autoAdvanceSeconds}s` }}
              onAnimationEnd={(e) => {
                setActiveIndex((activeIndex + 1) % props.items.length);
              }}
            />
          ) : null}

          <ListItemTitle
            onClick={() => {
              setActiveIndex(index);
              setShouldAutoAdvance(false);

              gtm.track('click', {
                location: 'Accordion',
                element_label: maybeNotSet(item.title),
                category: maybeNotSet(props.eventCategory),
              });
            }}
          >
            {item.title}
          </ListItemTitle>

          <ListItemContent>
            <div>{item.content}</div>

            {item.cta ? (
              <div>
                <ListItemCTA href={item.cta.href} onClick={item.cta.onClick}>
                  {item.cta.text} <span>→</span>
                </ListItemCTA>
              </div>
            ) : null}
          </ListItemContent>
        </ListItem>
      ))}
    </List>
  );
};

Accordion.defaultProps = {
  items: [],
  activeIndex: 0,
};

export default Accordion;

// Styled Components

export const List = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const progress = keyframes`
  0% {
    width: 0%;
  }
  
  100% {
    width: 100%;
  }
`;

export const ListItem = styled.li<{ animated: boolean }>`
  position: relative;

  &[data-expanded='true'] {
    border-top: 4px solid ${(props) => (props.animated ? COLORS.GRAY.TINT : COLORS.BLACK)};
    padding-top: 16px;
  }
`;

export const ListItemProgress = styled.div`
  position: absolute;
  top: -4px;
  left: 0;
  width: auto;
  height: 4px;
  background-color: ${COLORS.BLACK};
  animation-name: ${progress};
  animation-iteration-count: 1;
  animation-duration: var(--auto-advance-seconds);
  animation-timing-function: linear;
`;

export const ListItemTitle = styled.p`
  cursor: pointer;
  ${customTextSize(16, 21)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)}
  }
`;

export const ListItemContent = styled.div`
  ${customTextSize(14, 20)}
  margin-top: 20px;

  ${ListItem}[data-expanded="false"] & {
    display: none;
  }

  > * + * {
    margin-top: 20px;
  }

  a {
    text-decoration: underline;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(16, 24)}
    margin-top: 20px;
  }
`;

const ListItemCTA = styled(Link)`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  span {
    position: relative;
    top: -1px;
    display: inline-block;
    transition: transform 0.3s ease-in-out;
    transform: translateX(0);
  }

  &:hover span {
    transform: translateX(3px);
  }
`;
